import { useState } from 'react';
import { CardPlaceholder } from './CardPlaceholder';
import { tcgplayerCdnUrl } from './cardImageUrl';

/** Props for {@link CardImage}. */
export interface CardImageProps {
  /** Direct image URI stored on the cards row (highest priority). */
  imageUri?: string | null;
  /** TCGplayer product ID used to construct CDN URL when imageUri is absent. */
  tcgplayerProductId?: number | null;
  /** Alt text / fallback label shown in the placeholder. */
  label?: string;
  /** Width in pixels. Default 40. */
  w?: number;
  /** Height in pixels. Default 56. */
  h?: number;
}

/**
 * CardImage — shows real card art when available, gracefully falling back to
 * the striped CardPlaceholder.
 *
 * Shell: any.
 *
 * Priority: imageUri → TCGplayer CDN (from tcgplayerProductId) → CardPlaceholder.
 * Images are loaded lazily (`loading="lazy"`) so off-screen rows don't block
 * the network. On any load error the component drops back to the placeholder.
 *
 * Non-obvious contracts:
 *   - Uses `loading="lazy"` which pairs with the virtual list so only visible
 *     rows trigger image fetches.
 *   - `flexShrink: 0` matches CardPlaceholder so it doesn't collapse in flex parents.
 *   - `referrerPolicy="no-referrer"` (vendor#868) — every src here is a
 *     third-party host (the catalog CDN or a stored `image_uri`) that we
 *     never re-host; without this the vendor app's origin and path leak into
 *     the `Referer` header of every card-art request.
 */
export function CardImage({ imageUri, tcgplayerProductId, label, w = 40, h = 56 }: CardImageProps) {
  const src = imageUri ?? (tcgplayerProductId ? tcgplayerCdnUrl(tcgplayerProductId, w, h) : null);
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return <CardPlaceholder label={label} w={w} h={h} />;
  }

  return (
    <img
      src={src}
      alt={label ?? 'Card image'}
      width={w}
      height={h}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      style={{
        width: w,
        height: h,
        borderRadius: 'var(--radius-sm)',
        objectFit: 'cover',
        flexShrink: 0,
        display: 'block',
        border: '1px solid var(--mantine-color-default-border)',
      }}
    />
  );
}
